import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
  structuredData?: any;
  localBusiness?: boolean;
  animal?: {
    name: string;
    age: string;
    gender: string;
    breed?: string;
    description: string;
  };
}

const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  structuredData,
  localBusiness = false,
  animal
}) => {
  const siteUrl = 'https://adoptadinodai.ro'; // Production domain
  const siteName = 'Protecția Animalelor Sector 1 - Adăpost Odăi';
  const defaultTitle = 'Adoptă Câini București - Adăpost Protecția Animalelor Sector 1';
  const defaultDescription = 'Adoptă un câine de la Adăpostul Odăi, Sector 1 București. Câini vaccinați, microcipați și deparazitați căutând o familie iubitoare. Adopție gratuită!';
  const defaultKeywords = 'adopta caine, adopta caini, adopta caini bucuresti, adopta caini sector 1, adaspost caini, adaspost caini bucuresti, protectia animalelor, animale adopție, câini adopție bucurești, câini abandonați, adăpost câini odăi';
  const defaultImage = `${siteUrl}/logo-blue.svg`;

  const finalTitle = title ? `${title} - ${siteName}` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultImage;
  const finalUrl = url ? `${siteUrl}${url}` : siteUrl;

  // Generate local business structured data
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "AnimalShelter",
    "name": "Protecția Animalelor Sector 1",
    "alternateName": "Adăpost Odăi",
    "description": "Adăpost pentru câini în Sector 1, București. Oferim servicii de adopție gratuită pentru câini vaccinați și microcipați.",
    "url": siteUrl,
    "telephone": "+40750209751",
    "email": "protectiaanimalelor@primarias1.ro",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Sos. Odăi, nr. 3-5",
      "addressLocality": "Sector 1",
      "addressRegion": "București",
      "postalCode": "014103",
      "addressCountry": "RO"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "44.4877",
      "longitude": "26.0988"
    },
    "openingHours": [
      "Mo-Fr 10:00-18:00",
      "Sa 10:00-16:00"
    ],
    "priceRange": "Gratuit",
    "paymentAccepted": "Donații",
    "currenciesAccepted": "RON",
    "areaServed": [
      {
        "@type": "City",
        "name": "București"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Sector 1"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61572807361488",
      "https://www.instagram.com/protectiaanimalelor.sector1"
    ],
    "serviceType": "Adopție animale, Îngrijire animale, Servicii veterinare",
    "specialOpeningHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Sunday",
      "opens": "Închis",
      "closes": "Închis"
    }
  };

  // Generate animal structured data if animal info is provided
  const animalSchema = animal ? {
    "@context": "https://schema.org",
    "@type": "Animal",
    "name": animal.name,
    "description": animal.description,
    "gender": animal.gender === 'male' ? 'Masculin' : 'Feminin',
    "age": animal.age,
    "breed": animal.breed || "Metis",
    "location": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Sector 1, București",
        "addressCountry": "RO"
      }
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "RON",
      "availability": "https://schema.org/InStock",
      "description": "Adopție gratuită - câine vaccinat, microcipat și deparazitat"
    }
  } : null;

  // Organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteName,
    "url": siteUrl,
    "logo": `${siteUrl}/logo-blue.svg`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+40750209751",
      "contactType": "Adopții și informații",
      "availableLanguage": ["Romanian"]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Sos. Odăi, nr. 3-5",
      "addressLocality": "Sector 1",
      "addressRegion": "București",
      "postalCode": "014103",
      "addressCountry": "RO"
    },
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61572807361488",
      "https://www.instagram.com/protectiaanimalelor.sector1"
    ]
  };

  // Combine all schemas
  const schemas: any[] = [organizationSchema];
  if (localBusiness) schemas.push(localBusinessSchema);
  if (animalSchema) schemas.push(animalSchema);
  if (structuredData) schemas.push(structuredData);

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalUrl} />
      
      {/* Language and locale */}
      <html lang="ro" />
      <meta name="language" content="Romanian" />
      <meta name="geo.region" content="RO-B" />
      <meta name="geo.placename" content="Sector 1, București" />
      <meta name="geo.position" content="44.4877;26.0988" />
      <meta name="ICBM" content="44.4877, 26.0988" />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="ro_RO" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      
      {/* Additional Romanian SEO */}
      <meta name="subject" content="Adopția animalelor de companie în București" />
      <meta name="audience" content="Adulți care doresc să adopte animale de companie" />
      <meta name="coverage" content="București, România" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />
      
      {/* Local business verification */}
      <meta name="google-site-verification" content="your-google-verification-code" />
      
      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      
      {/* Preconnect to important domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      
      {/* DNS prefetch for external resources */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
    </Helmet>
  );
};

export default SEOHead; 